<template>
  <div :class="['drag-and-drop-file-uploader', customClass]">
    <div
      id="file-drop-area"
      class="drag-and-drop-file-uploader__file-drop-area"
      @dragover.prevent
      @dragenter.prevent
      @drop="handleFileDrop"
      @click="browseFiles"
    >
      <div class="drag-and-drop-file-uploader__d-n-d-text">
        <div v-if="dropAreaText">{{ dropAreaText }}</div>
        <div v-else v-html="dropAreaTextWithHTML"></div>
      </div>
      <div
        v-if="files.length"
        class="drag-and-drop-file-uploader__uploaded-file"
        @click.stop
      >
        <ul>
          <li
            v-for="(file, index) in files"
            :key="index"
            class="drag-and-drop-file-uploader__uploaded-file-item"
          >
            <div class="drag-and-drop-file-uploader__uploaded-file-name">
              <FileIcon
                class="drag-and-drop-file-uploader__uploaded-file-icon"
              />
              {{ file.name }}
            </div>
            <button
              class="drag-and-drop-file-uploader__uploaded-file-remove"
              @click="removeFile"
            >
              <TrashIcon
                class="drag-and-drop-file-uploader__uploaded-file-icon"
              />
            </button>
          </li>
        </ul>
      </div>
      <div class="drag-and-drop-file-uploader__instructions">
        <div v-if="instructions">{{ instructions }}</div>
        <div v-else v-html="instructionsWithHTML"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { FileIcon, TrashIcon } from 'vue-feather-icons'

export default {
  name: 'DragAndDropFileUploader',
  emits: ['files-uploaded', 'files-uploaded-validity'],
  components: {
    FileIcon,
    TrashIcon,
  },
  props: {
    customClass: {
      type: String,
      default: '',
    },
    dropAreaText: {
      type: String,
      default: 'Upload supporting documentation',
    },
    dropAreaTextWithHTML: {
      type: String,
      default: '',
    },
    instructions: {
      type: String,
      default: '',
    },
    instructionsWithHTML: {
      type: String,
      default: `JPG, PNG, and PDF formats, up to 10MB. Drag and drop files or <span class="drag-and-drop-file-uploader__instructions-link">browse</span>.`,
    },
    acceptedFileTypes: {
      type: Array,
      default: () => ['image/jpeg', 'image/png', 'application/pdf'],
    },
    maxFileSize: {
      type: Number,
      default: 10 * 1024 * 1024, // 10MB
    },
    notifications: {
      type: Boolean,
      default: true,
    },
    errorMessage: {
      type: Object,
      default: () => ({
        invalidType: 'Only JPG, PNG, and PDF formats are allowed.',
        fileSizeExceeded: 'File size should not exceed 10MB.',
        duplicateFile: 'File has already been uploaded.',
      }),
    },
  },
  data() {
    return {
      files: [],
      filesUploaded: false,
    }
  },
  methods: {
    handleFileDrop(event) {
      event.preventDefault()
      event.stopPropagation()
      const droppedFiles = Array.from(event.dataTransfer.files)
      this.processFiles(droppedFiles)
    },
    browseFiles() {
      const input = document.createElement('input')
      input.type = 'file'
      input.multiple = true
      input.accept = this.acceptedFileTypes.join(',')
      input.onchange = event => {
        const files = event.target.files
        this.processFiles(files)
      }
      input.click()
    },
    processFiles(files) {
      const newFiles = Array.from(files)
      const filesWeight = [...newFiles, ...this.files].reduce(
        (acc, file) => acc + file.size,
        0
      )
      const existingFiles = this.files.map(file => `${file.name}-${file.size}`)

      // Check for files size exceeded
      if (filesWeight > this.maxFileSize) {
        this.notifications &&
          this.$store.dispatch(
            'app/setError',
            this.errorMessage.fileSizeExceeded
          )
        this.$emit('file-error', this.errorMessage.fileSizeExceeded)
        return
      }

      for (let i = 0; i < newFiles.length; i++) {
        const file = newFiles[i]
        const fileType = file.type
        const fileIdentifier = `${file.name}-${file.size}`

        // Check for invalid file type
        if (!this.acceptedFileTypes.includes(fileType)) {
          this.notifications &&
            this.$store.dispatch('app/setError', this.errorMessage.invalidType)
          this.$emit('file-error', this.errorMessage.invalidType)
          return
        }

        // Check for duplicate file
        if (existingFiles.includes(fileIdentifier)) {
          this.notifications &&
            this.$store.dispatch(
              'app/setError',
              this.errorMessage.duplicateFile
            )
          this.$emit('file-error', this.errorMessage.duplicateFile)
          return
        }
      }

      this.files = this.files.concat(newFiles)
      this.filesUploaded = this.files.length > 0
      this.eventsEmitter()
    },
    removeFile(index) {
      this.files.splice(index, 1)
      this.filesUploaded = this.files.length > 0
      this.eventsEmitter()
    },
    eventsEmitter() {
      this.$emit('files-uploaded', this.files)
      this.$emit('files-uploaded-validity', this.files.length > 0)
    },
  },
}
</script>

<style lang="scss" scoped>
.drag-and-drop-file-uploader {
  &__file-drop-area {
    border-radius: 10px;
    border: 1px dashed var(--emperor-gray);
    background: var(--glass-mid-gray);
    padding: 40px;
    text-align: center;
    cursor: pointer;
    display: flex;
    flex-flow: column;
    gap: 15px;

    &.dragover {
      border-color: var(--black);
    }
    &:hover {
      border: 1px dashed var(--perfect-gray);
    }
    &:active {
      border-color: var(--flat-black);
      -webkit-box-shadow: var(--purple-dark-box-shadow);
      box-shadow: var(--purple-dark-box-shadow);
    }
    &:focus {
      border-color: var(--flat-black);
      -webkit-box-shadow: var(--purple-dark-box-shadow);
      box-shadow: var(--purple-dark-box-shadow);
    }
  }
  &__uploaded-file {
    &-item {
      a {
        color: var(--purple-pale);
        text-decoration: none;
        &:hover {
          text-decoration: underline;
        }
      }
    }
    &-name,
    &-remove {
      display: flex;
      align-items: center;
      color: var(--white);
      font-size: rem(12px);
      font-style: normal;
      font-weight: 450;
      line-height: rem(14px);
      gap: 5px;
    }
    ul {
      margin: 5px 0;
      padding-left: 0;
      list-style-type: none;
      display: flex;
      flex-flow: column;
      align-items: center;
      gap: 5px;
      li {
        display: flex;
        width: 100%;
        max-width: 590px;
        justify-content: space-between;
        border-radius: 5px;
        border: 1px solid var(--light-gray-darker);
        background: var(--glass-mid-gray);
        padding: 5px;
      }
    }
    &-remove {
      background: none;
      border: none;
      cursor: pointer;
      padding: 0;
    }
    &-icon {
      stroke: var(--white);
      width: 16px;
    }
  }
  &__d-n-d-text {
    color: var(--white);
    font-size: rem(14px);
    font-style: normal;
    font-weight: 500;
    line-height: rem(20px);
  }
  &__instructions {
    color: var(--light-blue-gray);
    font-size: rem(12px);
    font-style: normal;
    font-weight: 450;
    line-height: rem(14px);
  }
}
</style>
<style lang="scss">
.drag-and-drop-file-uploader__instructions-link {
  color: var(--purple-pale);
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
}
</style>
